import { useCallback, useEffect, useMemo, useState } from "react";

import useService from "../../../../../hooks/useService";
import { getModalHandler } from "../../../Common/Modals/hooks/useModalSingleton";
import {
  buildSelectableOptions,
  createEmailDraftRow,
  createPhoneDraftRow,
  getEmailTypeKey,
  getPhoneTypeKey,
  isDraftChanged,
  mergeEmailRow,
  mergePhoneRow,
  normalizeCollection,
  normalizeEditablePhoneValue,
  normalizeOption,
  normalizeText,
} from "../sections/additional-contact/additionalContactUtils";

function sortFieldOptions(response = []) {
  return response
    .slice()
    .sort((a, b) => Number(a?.key) - Number(b?.key))
    .map(normalizeOption);
}

export default function useProviderDetailAdditionalContactSection({
  permissionState,
  providerInformation,
  setProviderInformation,
}) {
  const enumRepository = useService("repositories/enum-repository");
  const providerRepository = useService("repositories/provider-repository");
  const providerEmailRepository = useService("repositories/provider-email-repository");
  const providerContactInfoRepository = useService("repositories/provider-contact-info-repository");
  const providerId = providerInformation?.pk ?? providerInformation?.id;
  const [emailTypeOptions, setEmailTypeOptions] = useState([]);
  const [phoneTypeOptions, setPhoneTypeOptions] = useState([]);
  const [emails, setEmails] = useState([]);
  const [phones, setPhones] = useState([]);
  const [loadingState, setLoadingState] = useState({ emails: false, phones: false, saving: false });
  const [editingState, setEditingState] = useState({ emails: false, phones: false });
  const [emailDraftRows, setEmailDraftRows] = useState([]);
  const [phoneDraftRows, setPhoneDraftRows] = useState([]);

  const providerEmailFallbackMap = useMemo(
    () => new Map(normalizeCollection(providerInformation?.emails ?? providerInformation?.provider_emails).map((item) => [String(item.id), item])),
    [providerInformation]
  );
  const providerPhoneFallbackMap = useMemo(
    () => new Map(normalizeCollection(providerInformation?.contact_infos ?? providerInformation?.contactInfos).map((item) => [String(item.id), item])),
    [providerInformation]
  );

  const setLoading = (fieldName, value) => {
    setLoadingState((prev) => ({ ...prev, [fieldName]: value }));
  };

  const loadEmails = useCallback(async () => {
    if (!providerId || !permissionState.canViewEmails) {return setEmails([]);}
    setLoading("emails", true);
    try {
      const response = await providerEmailRepository.query({ provider: providerId, ordering: "created_at", noCache: true });
      const nextRows = (response || []).map((item) => mergeEmailRow(item, providerEmailFallbackMap.get(String(item.id))));
      setEmails(nextRows.length ? nextRows : normalizeCollection(providerInformation?.emails ?? providerInformation?.provider_emails));
    } catch (error) {
      const fallbackRows = normalizeCollection(providerInformation?.emails ?? providerInformation?.provider_emails);
      if (fallbackRows.length) {setEmails(fallbackRows);}
      else {getModalHandler().openFailureModal(error);}
    } finally {
      setLoading("emails", false);
    }
  }, [permissionState.canViewEmails, providerEmailFallbackMap, providerEmailRepository, providerId, providerInformation]);

  const loadPhones = useCallback(async () => {
    if (!providerId || !permissionState.canViewPhones) {return setPhones([]);}
    setLoading("phones", true);
    try {
      const response = await providerContactInfoRepository.query({ related_provider: providerId, contact_type: 1, ordering: "created_at", noCache: true });
      const nextRows = (response || []).map((item) => mergePhoneRow(item, providerPhoneFallbackMap.get(String(item.id))));
      setPhones(nextRows.length ? nextRows : normalizeCollection(providerInformation?.contact_infos ?? providerInformation?.contactInfos));
    } catch (error) {
      const fallbackRows = normalizeCollection(providerInformation?.contact_infos ?? providerInformation?.contactInfos);
      if (fallbackRows.length) {setPhones(fallbackRows);}
      else {getModalHandler().openFailureModal(error);}
    } finally {
      setLoading("phones", false);
    }
  }, [permissionState.canViewPhones, providerContactInfoRepository, providerId, providerInformation, providerPhoneFallbackMap]);

  const loadFieldOptions = useCallback(async (contentType, fieldName, setter) => {
    try {
      const queryParams = new URLSearchParams({ content_type: contentType, field_name: fieldName, flat: "True" });
      const response = await enumRepository.query({ urlPath: `field_options/?${queryParams.toString()}` });
      setter(sortFieldOptions(response || []));
    } catch (error) {
      getModalHandler().openFailureModal(error);
    }
  }, [enumRepository]);

  useEffect(() => {
    setEditingState({ emails: false, phones: false });
    setEmailDraftRows([]);
    setPhoneDraftRows([]);
  }, [providerId]);

  useEffect(() => {
    loadEmails();
    loadPhones();
  }, [loadEmails, loadPhones]);

  useEffect(() => {
    if (providerId && permissionState.canViewFieldOptions && (permissionState.canViewEmails || permissionState.canAddEmails || permissionState.canEditEmails)) {
      loadFieldOptions("provideremail", "email_type", setEmailTypeOptions);
    } else {
      setEmailTypeOptions([]);
    }
  }, [loadFieldOptions, permissionState.canAddEmails, permissionState.canEditEmails, permissionState.canViewEmails, permissionState.canViewFieldOptions, providerId]);

  useEffect(() => {
    if (providerId && permissionState.canViewFieldOptions && (permissionState.canViewPhones || permissionState.canAddPhones || permissionState.canEditPhones)) {
      loadFieldOptions("providercontactinfo", "contact_label", setPhoneTypeOptions);
    } else {
      setPhoneTypeOptions([]);
    }
  }, [loadFieldOptions, permissionState.canAddPhones, permissionState.canEditPhones, permissionState.canViewFieldOptions, permissionState.canViewPhones, providerId]);

  const isSectionEditing = editingState.emails || editingState.phones;
  const usedEmailTypeKeys = new Set(
    (editingState.emails
      ? emailDraftRows
      : emails.map((row) => ({ typeKey: getEmailTypeKey(row) })))
      .map((row) => row.typeKey)
      .filter(Boolean)
  );
  const usedPhoneTypeKeys = new Set(
    (editingState.phones
      ? phoneDraftRows
      : phones.map((row) => ({ typeKey: getPhoneTypeKey(row) })))
      .map((row) => row.typeKey)
      .filter(Boolean)
  );
  const emailAddDisabled =
    !permissionState.canAddEmails ||
    !emailTypeOptions.length ||
    usedEmailTypeKeys.size >= emailTypeOptions.length;
  const phoneAddDisabled =
    !permissionState.canAddPhones ||
    !phoneTypeOptions.length ||
    usedPhoneTypeKeys.size >= phoneTypeOptions.length;

  const enterEditMode = () => {
    if (!editingState.emails && (permissionState.canEditEmails || permissionState.canAddEmails || permissionState.canDeleteEmails)) {
      setEditingState((prev) => ({ ...prev, emails: true }));
      setEmailDraftRows(emails.map(createEmailDraftRow));
    }
    if (!editingState.phones && (permissionState.canEditPhones || permissionState.canAddPhones || permissionState.canDeletePhones)) {
      setEditingState((prev) => ({ ...prev, phones: true }));
      setPhoneDraftRows(phones.map(createPhoneDraftRow));
    }
  };

  const updateDraftRows = (setter) => (localId, fieldName, value) =>
    setter((prev) => prev.map((row) => (row.localId === localId ? { ...row, [fieldName]: value } : row)));
  const removeDraftRow = (setter, canDelete) => (rowToRemove) => {
    if (rowToRemove?.id && !canDelete) {return;}
    setter((prev) => prev.filter((row) => row.localId !== rowToRemove.localId));
  };

  const cancelSection = () => {
    setEditingState({ emails: false, phones: false });
    setEmailDraftRows([]);
    setPhoneDraftRows([]);
  };

  const hasChanges = (draftRows, existingRows, isEditing) =>
    isEditing && (draftRows.filter((row) => row.id).length !== existingRows.length || draftRows.some(isDraftChanged));
  const hasValidationError = (draftRows, isEditing) =>
    isEditing && draftRows.some((row) => !normalizeText(row.typeKey) || !normalizeText(row.value));

  const persistEmailChanges = async () => {
    const existingIds = emails.map((email) => email.id);
    const keptIds = emailDraftRows.filter((row) => row.id).map((row) => row.id);
    for (const id of existingIds.filter((id) => !keptIds.includes(id))) {
      if (permissionState.canDeleteEmails) {await providerEmailRepository.delete(id);}
    }
    for (const row of emailDraftRows) {
      const payload = { email_type: row.typeKey, email: normalizeText(row.value) };
      if (row.mode === "add" && permissionState.canAddEmails) {await providerEmailRepository.createRecord({ provider: providerId, ...payload });}
      else if (isDraftChanged(row) && permissionState.canEditEmails) {await providerEmailRepository.patch(row.id, payload);}
    }
  };

  const persistPhoneChanges = async () => {
    const existingIds = phones.map((phone) => phone.id);
    const keptIds = phoneDraftRows.filter((row) => row.id).map((row) => row.id);
    for (const id of existingIds.filter((id) => !keptIds.includes(id))) {
      if (permissionState.canDeletePhones) {await providerContactInfoRepository.delete(id);}
    }
    for (const row of phoneDraftRows) {
      const payload = { related_provider: providerId, contact_type: 1, contact_label: row.typeKey, display: normalizeEditablePhoneValue(row.value) };
      if (row.mode === "add" && permissionState.canAddPhones) {await providerContactInfoRepository.createRecord({ provider: providerId, ...payload });}
      else if (isDraftChanged(row) && permissionState.canEditPhones) {await providerContactInfoRepository.patch(row.id, payload);}
    }
  };

  const saveSection = async () => {
    setLoading("saving", true);
    try {
      if (hasChanges(emailDraftRows, emails, editingState.emails)) {await persistEmailChanges();}
      if (hasChanges(phoneDraftRows, phones, editingState.phones)) {await persistPhoneChanges();}
      const refreshedProvider = await providerRepository.findRecord(providerId, { noCache: true });
      const latestEmails = normalizeCollection(refreshedProvider?.emails ?? refreshedProvider?.provider_emails);
      const latestPhones = normalizeCollection(refreshedProvider?.contact_infos ?? refreshedProvider?.contactInfos);
      setEmails(latestEmails);
      setPhones(latestPhones);
      setProviderInformation?.((prev) => ({ ...prev, ...refreshedProvider, emails: latestEmails, provider_emails: latestEmails, contact_infos: latestPhones, contactInfos: latestPhones }));
      cancelSection();
      getModalHandler().openSuccessModal();
    } catch (error) {
      getModalHandler().openFailureModal(error);
    } finally {
      setLoading("saving", false);
    }
  };

  return {
    emailAddDisabled,
    phoneAddDisabled,
    emailDraftRows,
    phoneDraftRows,
    emailTypeOptions,
    phoneTypeOptions,
    emails,
    phones,
    loadingState,
    editingState,
    isSectionEditing,
    canRender: permissionState.canViewEmails || permissionState.canViewPhones,
    saveDisabled:
      loadingState.saving ||
      hasValidationError(emailDraftRows, editingState.emails) ||
      hasValidationError(phoneDraftRows, editingState.phones) ||
      (!hasChanges(emailDraftRows, emails, editingState.emails) &&
        !hasChanges(phoneDraftRows, phones, editingState.phones)),
    enterEditMode,
    cancelSection,
    saveSection,
    addEmailRow: () => setEmailDraftRows((prev) => [...prev, createEmailDraftRow()]),
    addPhoneRow: () => setPhoneDraftRows((prev) => [...prev, createPhoneDraftRow()]),
    updateEmailRow: updateDraftRows(setEmailDraftRows),
    updatePhoneRow: updateDraftRows(setPhoneDraftRows),
    removeEmailRow: removeDraftRow(setEmailDraftRows, permissionState.canDeleteEmails),
    removePhoneRow: removeDraftRow(setPhoneDraftRows, permissionState.canDeletePhones),
    getEmailRowOptions: (row) => buildSelectableOptions(emailTypeOptions, emailDraftRows, row),
    getPhoneRowOptions: (row) => buildSelectableOptions(phoneTypeOptions, phoneDraftRows, row),
  };
}
